import React, { useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { MoreHorizontal } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Sheet, SheetContent, SheetTrigger, SheetClose } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { useAdminRole } from '@/hooks/useAdminRole';
import { useAstrologyAccess } from '@/features/astrology/hooks/useAstrologyAccess';
import { useAssessments } from '@/hooks/useAssessments';
import { NAVIGATION_GROUPS, navigationItemIsActive, visibleNavigationItems } from '@/config/navigation';

function tapHaptic() {
  if (typeof navigator !== 'undefined' && 'vibrate' in navigator) {
    try { navigator.vibrate(8); } catch { /* unsupported */ }
  }
}

export const MobileBottomNav: React.FC = () => {
  const location = useLocation();
  const [moreOpen, setMoreOpen] = useState(false);
  const { isAdmin } = useAdminRole();
  const { enabled: astrologyEnabled } = useAstrologyAccess();
  const { hasTPSAssessment } = useAssessments();
  const items = useMemo(() => visibleNavigationItems({ isAdmin, hasTPSAssessment, astrologyEnabled }), [isAdmin, hasTPSAssessment, astrologyEnabled]);
  const primary = items.filter((item) => item.primaryMobile);
  const moreItems = items.filter((item) => !item.primaryMobile);
  const moreActive = moreItems.some((item) => navigationItemIsActive(item, location.pathname));

  const closeAndTap = () => { tapHaptic(); setMoreOpen(false); };

  return (
    <nav aria-label="Main navigation" className="fixed inset-x-0 bottom-0 z-50 border-t border-border bg-card/95 pb-[env(safe-area-inset-bottom)] backdrop-blur-xl md:hidden">
      <div className="flex h-16 items-center justify-around px-2 touch-manipulation">
        {primary.map((item) => {
          const active = navigationItemIsActive(item, location.pathname);
          return (
            <Link key={item.id} to={item.path} onClick={tapHaptic} aria-label={item.label} aria-current={active ? 'page' : undefined} className={cn('relative flex min-h-12 min-w-14 flex-col items-center justify-center gap-0.5 rounded-lg px-2 py-1.5 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/60 active:scale-95 motion-reduce:transform-none', active ? 'text-primary' : 'text-muted-foreground')}>
              {active && <span aria-hidden className="absolute top-0 h-0.5 w-7 rounded-full bg-primary" />}
              <span className={cn('rounded-lg p-1.5', active && 'bg-primary/10')}><item.icon className="h-5 w-5" /></span>
              <span className="text-[11px] font-medium leading-none">{item.shortLabel ?? item.label}</span>
            </Link>
          );
        })}
        <Sheet open={moreOpen} onOpenChange={setMoreOpen}>
          <SheetTrigger asChild>
            <Button variant="ghost" onClick={tapHaptic} aria-label="More destinations" aria-current={moreActive ? 'page' : undefined} className={cn('relative flex min-h-12 min-w-14 flex-col items-center justify-center gap-0.5 rounded-lg px-2 py-1.5 text-muted-foreground', moreActive && 'text-primary')}>
              {moreActive && <span aria-hidden className="absolute top-0 h-0.5 w-7 rounded-full bg-primary" />}
              <span className={cn('rounded-lg p-1.5', moreActive && 'bg-primary/10')}><MoreHorizontal className="h-5 w-5" /></span>
              <span className="text-[11px] font-medium leading-none">More</span>
            </Button>
          </SheetTrigger>
          <SheetContent side="bottom" className="max-h-[85vh] overflow-y-auto rounded-t-2xl px-4 pb-[max(env(safe-area-inset-bottom),1rem)] pt-3">
            <div aria-hidden className="mx-auto mb-3 h-1 w-10 rounded-full bg-muted-foreground/30" />
            <div className="mb-3 flex items-center justify-between"><h2 className="text-base font-semibold">Explore Psyforge</h2><SheetClose asChild><Button variant="ghost" size="sm">Done</Button></SheetClose></div>
            {NAVIGATION_GROUPS.map((group) => {
              const grouped = moreItems.filter((item) => item.group === group);
              if (grouped.length === 0) return null;
              return (
                <section key={group} className="border-t border-border/40 py-3" aria-labelledby={`mobile-nav-${group}`}>
                  <h3 id={`mobile-nav-${group}`} className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{group}</h3>
                  <div className="grid grid-cols-2 gap-2">
                    {grouped.map((item) => {
                      const active = navigationItemIsActive(item, location.pathname);
                      const content = <><item.icon className="h-5 w-5 shrink-0" /><span className="min-w-0 text-sm font-medium">{item.shortLabel ?? item.label}</span></>;
                      const classes = cn('flex min-h-14 items-center gap-3 rounded-lg border p-3 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/60', active ? 'border-primary/30 bg-primary/10 text-primary' : 'border-border/40 bg-card/50 text-foreground active:bg-muted/50');
                      return item.external ? <a key={item.id} href={item.path} target="_blank" rel="noopener noreferrer" onClick={closeAndTap} className={classes}>{content}</a> : <Link key={item.id} to={item.path} onClick={closeAndTap} aria-current={active ? 'page' : undefined} className={classes}>{content}</Link>;
                    })}
                  </div>
                </section>
              );
            })}
          </SheetContent>
        </Sheet>
      </div>
    </nav>
  );
};
